import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  CssBaseline,
  Container,
  useMediaQuery,
  useTheme,
  Drawer,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Badge,
  Fab,
  Slide,
  alpha,
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon
} from '@mui/material';
import {
  Menu as MenuIcon,
  Search as SearchIcon,
  ShoppingCart as ShoppingCartIcon,
  Home as HomeIcon,
  Category as CategoryIcon,
  Favorite as FavoriteIcon,
  Person as PersonIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon
} from '@mui/icons-material';
import ThemeProvider from './ThemeProvider';
import MobileMegaMenu from './MobileMegaMenu';
import CartDrawer from './CartDrawer';
import MobileSearchBar from './MobileSearchBar';
import MobileBottomNavigation from './MobileBottomNavigation';
import { useStoreSettings } from '../../contexts/StoreSettingsContext';
import { useCart } from '../../contexts/CartContext';
// دالة بديلة لإنشاء الأيقونة
const createDynamicFavicon = () => '/favicon.ico';

const MobileOptimizedStoreLayout: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { settings } = useStoreSettings();
  const { cart } = useCart();
  const getTotalItems = () => cart.totalItems;

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | undefined>(undefined);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [speedDialOpen, setSpeedDialOpen] = useState(false);

  // مراقبة التمرير لإظهار زر العودة للأعلى
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // تحديث الأيقونة عند تغيير الصفحة
  useEffect(() => {
    // يمكن إضافة منطق تحديث الأيقونة هنا
  }, [location.pathname]);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setSearchOpen(false);
  };

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleCartToggle = () => {
    setCartOpen(!cartOpen);
  };

  const handleSearchToggle = () => {
    setSearchOpen(!searchOpen);
  };

  const handleCategorySelect = (categoryId: string) => {
    setSelectedCategoryId(categoryId);
    setMobileMenuOpen(false);
    navigate('/categories');
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const speedDialActions = [
    {
      icon: <HomeIcon />,
      name: 'الرئيسية',
      onClick: () => navigate('/')
    },
    {
      icon: <CategoryIcon />,
      name: 'الأقسام',
      onClick: () => navigate('/categories')
    },
    {
      icon: <FavoriteIcon />,
      name: 'المفضلة',
      onClick: () => navigate('/favorites')
    },
    {
      icon: <PersonIcon />,
      name: 'حسابي',
      onClick: () => navigate('/account')
    }
  ];

  return (
    <ThemeProvider>
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <CssBaseline />

        {/* شريط التطبيق العلوي للهاتف */}
        {isMobile && (
          <AppBar
            position="fixed"
            className="store-header"
            elevation={0}
            sx={{
              bgcolor: alpha(theme.palette.background.paper, 0.95),
              backdropFilter: 'blur(10px)',
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              color: theme.palette.text.primary
            }}
          >
            <Toolbar sx={{ justifyContent: 'space-between', minHeight: '56px !important' }}>
              <IconButton
                edge="start"
                color="inherit"
                onClick={handleMobileMenuToggle}
                sx={{
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.2) }
                }}
              >
                <MenuIcon />
              </IconButton>

              <Typography
                variant="h6"
                component="div"
                sx={{
                  fontWeight: 'bold',
                  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}
              >
                {settings?.general?.storeName || 'المتجر الإلكتروني'}
              </Typography>

              <Box sx={{ display: 'flex', gap: 1 }}>
                <IconButton
                  color="inherit"
                  onClick={handleSearchToggle}
                  sx={{
                    bgcolor: alpha(theme.palette.info.main, 0.1),
                    '&:hover': { bgcolor: alpha(theme.palette.info.main, 0.2) }
                  }}
                >
                  <SearchIcon />
                </IconButton>

                <IconButton
                  color="inherit"
                  onClick={handleCartToggle}
                  sx={{
                    bgcolor: alpha(theme.palette.success.main, 0.1),
                    '&:hover': { bgcolor: alpha(theme.palette.success.main, 0.2) }
                  }}
                >
                  <Badge badgeContent={getTotalItems()} color="error">
                    <ShoppingCartIcon />
                  </Badge>
                </IconButton>
              </Box>
            </Toolbar>
          </AppBar>
        )}

        {/* قائمة الجوال */}
        <Drawer
          anchor="right"
          open={mobileMenuOpen}
          onClose={handleMobileMenuToggle}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: '85%',
              maxWidth: 320,
              boxShadow: `0 0 30px ${alpha(theme.palette.common.black, 0.2)}`,
              borderTopLeftRadius: 16,
              borderBottomLeftRadius: 16,
              background: `linear-gradient(180deg, ${alpha(theme.palette.background.paper, 0.95)} 0%, ${alpha(theme.palette.background.default, 0.95)} 100%)`,
              backdropFilter: 'blur(20px)'
            },
          }}
        >
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%'
          }}>
            <MobileMegaMenu
              onCategorySelect={handleCategorySelect}
              onClose={handleMobileMenuToggle}
            />
          </Box>
        </Drawer>

        {/* سلة التسوق */}
        <CartDrawer
          open={cartOpen}
          onClose={() => setCartOpen(false)}
        />

        {/* شريط البحث للهاتف */}
        <MobileSearchBar
          open={searchOpen}
          onClose={() => setSearchOpen(false)}
          onSearch={handleSearch}
        />

        {/* محتوى الصفحة */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            width: '100%',
            pt: isMobile ? '56px' : 0,
            pb: isMobile ? '56px' : 0,
            px: { xs: 1, sm: 2, md: 3 },
            py: 2
          }}
        >
          <Container
            maxWidth={false}
            disableGutters={isMobile}
            sx={{
              px: isMobile ? 1 : { sm: 2, md: 3 }
            }}
          >
            <Outlet context={{ searchQuery, selectedCategoryId }} />
          </Container>
        </Box>

        {/* شريط التنقل السفلي للهاتف */}
        {isMobile && (
          <MobileBottomNavigation />
        )}

        {/* زر العودة للأعلى */}
        <Slide in={showScrollTop} direction="up" timeout={300}>
          <Fab
            color="primary"
            size="medium"
            onClick={scrollToTop}
            sx={{
              position: 'fixed',
              bottom: isMobile ? 80 : 20,
              left: 20,
              zIndex: 1000,
              boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.3)}`
            }}
          >
            <KeyboardArrowUpIcon />
          </Fab>
        </Slide>

        {/* قائمة الإجراءات السريعة */}
        {!isMobile && (
          <SpeedDial
            ariaLabel="قائمة الإجراءات السريعة"
            sx={{
              position: 'fixed',
              bottom: 20,
              right: 20,
              zIndex: 1000
            }}
            icon={<SpeedDialIcon />}
            onClose={() => setSpeedDialOpen(false)}
            onOpen={() => setSpeedDialOpen(true)}
            open={speedDialOpen}
          >
            {speedDialActions.map((action) => (
              <SpeedDialAction
                key={action.name}
                icon={action.icon}
                tooltipTitle={action.name}
                onClick={action.onClick}
                sx={{
                  '& .MuiSpeedDialAction-fab': {
                    bgcolor: alpha(theme.palette.primary.main, 0.9),
                    '&:hover': {
                      bgcolor: theme.palette.primary.main
                    }
                  }
                }}
              />
            ))}
          </SpeedDial>
        )}
      </Box>
    </ThemeProvider>
  );
};

export default MobileOptimizedStoreLayout;
